import { useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import CircularLoading from "components/CircularLoading";
import { Competency_Map_Status, useCompetencyMapGenQuestionsLogQuery, useCompetencyMapQuery } from "@generated/graphql";
import useIsCreate from "hooks/useIsCreate";
import useSetTabTitle from "hooks/useSetTabTitle";
import TabLayout, { TabLayoutProps } from "layout/TabLayout";
import TestingListTab from "pages/CompetencyMaps/view/tabs/TestingListTab";
import { getTestitngTypeTitle, secondsToMinutes } from "helpers/index";
import { Competency_Map_Tabs_Values } from "constants/global";
import { Box, IconButton } from "@mui/material";
import { WysiwygOutlined } from "@mui/icons-material";
import useUpdateCompetencyMapStatus from "hooks/useUpdateCompetencyMapStatus";
import Header from "./components/Header";
import { CompetencyMapInputs } from "./components/types";
import ConstructorTab from "./tabs/ConstructorTab";
import QuestionsListTab from "./tabs/QuestionsListTab";
import NavigationSidebar from "./components/NavigationSidebar";

const CompetencyMapView = () => {
  const headerRef = useRef<HTMLDivElement>(null);
  const isCreate = useIsCreate();
  const [isEditable, setIsEditable] = useState<boolean>(isCreate);
  const { mapId } = useParams();
  const { data, loading, refetch } = useCompetencyMapQuery({
    variables: { competencyMapId: mapId! },
    skip: !mapId,
  });

  const { data: competencyMapGenQuestionsLogsData, refetch: refetchCompetencyMapGenQuestionsLog } =
    useCompetencyMapGenQuestionsLogQuery({ variables: { competencyMapId: mapId! } });

  const competencyMap = data?.competencyMap;
  const form = useForm<CompetencyMapInputs>({
    values: {
      id: competencyMap?.id,
      title: competencyMap?.title || "",
      comment: competencyMap?.comment,
      specialization: competencyMap?.specialization || "",
      grades: competencyMap?.grades?.join(", "),
      updatedAt: competencyMap?.updatedAt,
      createdAt: competencyMap?.createdAt,
      isRepositoryCheck: competencyMap?.isRepositoryCheck || false,
      questionsTime: secondsToMinutes(competencyMap?.questionsTime),
      testitngType: getTestitngTypeTitle(competencyMap?.questionsTime),
      knowledgeBlocks: competencyMap?.competences?.map((block) => ({
        id: block.id,
        title: block.title,
        knowledgeBlockCompetences: block.knowledgeBlockCompetences?.map((competence) => ({
          id: competence.knowledgeBlock.id,
          title: competence.knowledgeBlock.title,
          skills: competence.knowledgeBlock.skills,
          profession: competence.knowledgeBlock.profession,
        })),
      })),
    },
  });

  const disabled = competencyMap?.status === Competency_Map_Status.Generating;

  const { control } = form;
  const fieldArray = useFieldArray({
    control,
    name: "knowledgeBlocks",
    keyName: "formId",
  });

  const { append } = fieldArray;

  const title = `Карта компетенций ${competencyMap?.title ? ` «${competencyMap.title}»` : ""}`;
  useSetTabTitle(title, competencyMap?.title);

  const { handleArchiveCompetencyMap, handleUpdateCompetencyMapStatus } = useUpdateCompetencyMapStatus({
    competencyMap,
  });

  const TABS: TabLayoutProps["tabs"] = [
    {
      id: Competency_Map_Tabs_Values.constructor,
      title: "Структура КК",
      component: (
        <ConstructorTab
          fieldArray={fieldArray}
          form={form}
          isEditable={isEditable}
          setIsEditable={setIsEditable}
          competencyMap={competencyMap}
          disabled={disabled}
          refetchCompetencyMap={refetch}
          competencyMapGenQuestionsLogs={competencyMapGenQuestionsLogsData?.competencyMapGenQuestionsLog}
          refetchCompetencyMapGenQuestionsLog={refetchCompetencyMapGenQuestionsLog}
          handleUpdateMapStatus={handleUpdateCompetencyMapStatus}
          handleArchiveMap={handleArchiveCompetencyMap}
        />
      ),
      sx: { marginTop: 1, "& > div": { px: 0 } },
    },
    {
      id: Competency_Map_Tabs_Values.questionsList,
      title: "Список вопросов",
      component: <QuestionsListTab />,
      disabled: isCreate,
      sx: { "& > div": { px: 0 } },
    },
    {
      id: Competency_Map_Tabs_Values.testingList,
      title: "Тестирования",
      component: <TestingListTab />,
      disabled: isCreate,
      sx: { "& > div": { px: 0 } },
    },
  ];

  const [navigationOpen, setNavigationOpen] = useState(false);

  if (loading) return <CircularLoading />;

  return (
    <>
      <Header
        competencyMap={competencyMap}
        disabled={disabled}
        append={append}
        isCreate={isCreate}
        isEditable={isEditable}
        headerRef={headerRef}
        refetchCompetencyMapGenQuestionsLog={refetchCompetencyMapGenQuestionsLog}
        handleArchiveMap={handleArchiveCompetencyMap}
      />
      <Box sx={{ position: "relative" }}>
        <IconButton
          sx={{ position: "absolute", top: -6, left: -55 }}
          onClick={() => setNavigationOpen(true)}>
          <WysiwygOutlined fontSize='large' />
        </IconButton>
        <NavigationSidebar
          open={navigationOpen}
          handleClose={() => setNavigationOpen(false)}
          competencyMap={competencyMap}
        />
        <TabLayout
          tabs={TABS}
          defaultTab='constructor'
          sx={{
            "& .MuiTabPanel-root > div": {
              backgroundColor: "transparent",
            },
          }}
          withSearchParams
        />
      </Box>
    </>
  );
};

export default CompetencyMapView;
